import { containedAppointmentChildStart } from "./containedAppointmentTiming";
import moment from "moment";

import { resolveAppointmentAdditionalServiceCost } from "./appointmentAddonPrice";
import { childResourceOptionState } from "./appointmentChildResourceOptions";
import {
  buildAppointmentChildOptionState,
  buildAppointmentServiceFields,
  isPresent,
  resolveAppointmentServiceStaffId,
} from "../../pages/calendar/appointmentServiceFields";

const succeeded = (action) => action?.meta?.requestStatus === "fulfilled";

const failureMessage = (action, fallback) => {
  const payload = action?.payload;
  if (typeof payload === "string" && payload.trim()) return payload;
  if (typeof payload?.message === "string" && payload.message.trim()) return payload.message;
  if (typeof payload?.message?.message === "string" && payload.message.message.trim()) {
    return payload.message.message;
  }
  return action?.error?.message || fallback;
};

const durationMinutes = (value) => {
  const duration = moment.duration(value || "00:00");
  return duration.isValid() ? duration.asMinutes() : 0;
};

export const appointmentAdditionalServiceFormName = (formType) =>
  `appointmentForm/${formType === "edit_appointment" ? "edit" : "add"}/addanotherservices/service_id`;

export const appointmentAdditionalServiceEndTime = (formValues, childIndex) => {
  const totalMinutes = [
    formValues?.durationWithPadding,
    ...(Array.isArray(formValues?.addonservices)
      ? formValues.addonservices.map((item) => item?.durationWithPadding)
      : []),
    ...(Array.isArray(formValues?.addanotherservices)
      ? formValues.addanotherservices
        .slice(0, childIndex)
        .map((item) => item?.durationWithPadding)
      : []),
  ].reduce((sum, value) => sum + durationMinutes(value), 0);

  const start = moment(
    `${formValues?.dateof || ""} ${formValues?.start_time || ""}`,
    "dddd, DD MMMM YYYY HH:mm",
    true,
  );
  const containedStart = containedAppointmentChildStart(formValues, "addanotherservices", childIndex, null);
  if (containedStart) return containedStart;
  return start.isValid()
    ? start.add(totalMinutes, "minutes").format("YYYY-MM-DD HH:mm:00")
    : "";
};

/**
 * Loads price, staff and time-filtered resource options for one Add Another
 * Service selection before callers commit Formik or Redux child state.
 */
export const loadAppointmentAdditionalServiceSelection = async ({
  childIndex,
  existingOptionState = {},
  formValues,
  loaders,
  parentAppointmentEndTime,
  parentStaffOptions = [],
  serviceId,
}) => {
  const effectiveStaffId = resolveAppointmentServiceStaffId({}, formValues?.staff_id);
  if (!isPresent(serviceId) || !isPresent(effectiveStaffId)) {
    return {
      status: "error",
      error: "Select a primary service and staff before adding another service.",
    };
  }

  let priceAction;
  try {
    priceAction = await loaders.loadPrice({
      index_id: childIndex,
      parent_service_id: formValues?.service_id,
      parent_staff_id: effectiveStaffId,
      service_id: serviceId,
      staff_id: effectiveStaffId,
    });
  } catch (error) {
    return { status: "error", error: error?.message || "Unable to load the selected service price." };
  }
  if (!succeeded(priceAction)) {
    return { status: "error", error: failureMessage(priceAction, "Unable to load the selected service price.") };
  }

  let serviceData = priceAction.payload || {};
  let fields = buildAppointmentServiceFields(
    { ...serviceData, service_id: serviceData.id ?? serviceId },
    effectiveStaffId,
  );
  const completePayload = {
    ...(formValues?.addanotherservices?.[childIndex] || {}),
    remove_addon: formValues?.remove_addon || [],
    remove_addanother: formValues?.remove_addanother || [],
    parent_id: formValues?.id || "",
    service_id: serviceData.id ?? serviceId,
    staff_id: fields.staff_id || effectiveStaffId,
    dateof: formValues?.dateof,
    parentAppointmentEndTime:
      parentAppointmentEndTime ?? appointmentAdditionalServiceEndTime(formValues, childIndex),
    start_datetime: parentAppointmentEndTime ?? appointmentAdditionalServiceEndTime(formValues, childIndex),
    duration: fields.duration,
    padding_time: fields.padding_time,
    durationWithPadding: fields.durationWithPadding,
    listview: "Appointment",
    formName: appointmentAdditionalServiceFormName(formValues?.form_types),
  };
  let staffAction;
  let resourceAction;
  let bookedTherapistDropped = false;
  try {
    staffAction = await loaders.loadStaff({
      option: { valueField: "users.id", labelField: "CONCAT(users.first_name,' ',users.last_name)" },
      service_id: completePayload.service_id,
      pagetype: "Booking",
      availebleTimeObj: { ...completePayload },
    });
    if (!succeeded(staffAction)) {
      return { status: "error", error: failureMessage(staffAction, "Unable to load staff for the selected service.") };
    }
    const eligible = Array.isArray(staffAction.payload) ? staffAction.payload : [];
    const eligibleIds = new Set(
      eligible
        .map((option) => (option && typeof option === "object" ? option.value ?? option.id : option))
        .filter(isPresent)
        .map(String),
    );
    const bookedTherapistIsEligible = isPresent(effectiveStaffId)
      && eligibleIds.has(String(effectiveStaffId));
    const selectedStaffId = bookedTherapistIsEligible
      ? effectiveStaffId
      : eligible.length === 1 ? eligible[0].value : "";
    bookedTherapistDropped = isPresent(effectiveStaffId)
      && !bookedTherapistIsEligible
      && !selectedStaffId;
    if (selectedStaffId && String(selectedStaffId) !== String(effectiveStaffId)) {
      const selectedPrice = await loaders.loadPrice({
        index_id: childIndex, parent_service_id: formValues?.service_id,
        parent_staff_id: effectiveStaffId, service_id: serviceId, staff_id: selectedStaffId,
      });
      if (!succeeded(selectedPrice)) {
        return { status: "error", error: failureMessage(selectedPrice, "Unable to load the selected service price.") };
      }
      serviceData = selectedPrice.payload || {};
      fields = buildAppointmentServiceFields({ ...serviceData, service_id: serviceId });
    }
    fields = { ...fields, staff_id: selectedStaffId };
    Object.assign(completePayload, fields);
    resourceAction = await loaders.loadResources({
      option: { valueField: "id", labelField: "resource_item" },
      service_id: completePayload.service_id, pagetype: "Booking",
      isNotInResourceIds: [], availebleTimeObj: completePayload,
    });
  } catch (error) {
    return { status: "error", error: error?.message || "Unable to load the selected additional service." };
  }

  const failed = [
    [staffAction, "Unable to load staff for the selected service."],
    [resourceAction, "Unable to load resources for the selected service."],
  ].find(([action]) => !succeeded(action));
  if (failed) {
    return { status: "error", error: failureMessage(failed[0], failed[1]) };
  }

  const {
    resourceOptions: resourceItems,
    resource_requirements: requirements,
    resource_required: resourceRequired,
    resource_unavailable: resourceUnavailable,
  } = childResourceOptionState(resourceAction.payload, serviceData);
  const childId = Number(childIndex) + 1;
  const child = {
    ...(formValues?.addanotherservices?.[childIndex] || {}),
    ...fields,
    service_id: serviceData.id ?? serviceId,
    service_name: serviceData.name || "",
    parent_id: formValues?.id || null,
    start_datetime: completePayload.start_datetime,
    parentAppointmentEndTime: completePayload.parentAppointmentEndTime,
    resource_item_id: "",
    resource_allocations: [],
    resource_requirements: requirements,
    resource_required: resourceRequired,
    resource_unavailable: resourceUnavailable,
    cost: resolveAppointmentAdditionalServiceCost(serviceData),
  };
  const optionState = {
    ...buildAppointmentChildOptionState({
      existing: existingOptionState,
      id: childId,
      service: { ...serviceData, staff_id: fields.staff_id },
      parentStaffId: "",
      parentStaffOptions,
      staffOptions: staffAction.payload,
      resourceOptions: resourceItems,
    }),
    resource_required: resourceRequired,
    resource_unavailable: resourceUnavailable,
    serviceprice: serviceData,
    duration: fields.duration,
    padding_time: fields.padding_time,
    durationWithPadding: fields.durationWithPadding,
  };

  return {
    status: "ready",
    child,
    optionState,
    serviceData,
    availabilityPayload: completePayload,
    ...(bookedTherapistDropped
      ? { notice: "The booked therapist does not offer this added service. Choose another therapist." }
      : {}),
  };
};
